import {
	createAsyncThunk,
	createSlice,
	createSelector
} from "@reduxjs/toolkit"
import axios from "axios"
import { pasteCopy } from "./workspaceNoteSlice"
import {ensureWorkspaceTemplate} from "../utils/reduxUtilities"

// TODO: post-refactor
//  this slice has error values set for each byWorkspace entry, but nothing consumes it
export const newSearch = createAsyncThunk(
	"workspaceSearch/makeNewSearch",
	async ({ workspaceId, query }, {getState, rejectWithValue}) => {
		const { workspaceSearch } = getState()
		const workspace = workspaceSearch.byWorkspace?.[workspaceId]
		// the next Index will be used by the scroll slice to track the tab's scroll position.
		const nextIndex = workspace.searches.length
		try {
			// this url is only used for workspace searches. You'd think that would be obvious, but its not??
			const { data } = await axios.get(`/api/workspace/${workspaceId}/note?search=${query}`)
				.catch(e => console.error("NEW WORKSPACE SEARCH FAILED: ", e))
			return {
				query,
				results: data.notes,
				resultTreeIds: data.noteIds, //noteIds is built as a Set to ensure each ID is only represented once
				nextIndex,
				workspaceId
			}
		} catch (error) {
			console.error("CREATING NEW WORKSPACE SEARCH FAILED: ", error)
			rejectWithValue(error)
		}
	}
)

export const updateSearches = createAsyncThunk(
	"workspaceSearch/updateSearches",
	async (workspaceId, { getState, dispatch, rejectWithValue }) => {
		const { workspaceSearch } = getState()
		const workspace = workspaceSearch.byWorkspace?.[workspaceId]

		if (!workspace || workspace.searches.length === 0) {
			return rejectWithValue("no searches to update")
		}

		try {
			for (let i = 0; i < workspace.searches.length; i++) {
				const search = workspace.searches[i]
				const { data } = await axios.get(
					`/api/workspace/${workspaceId}/note?search=${search.query}`
				)
				dispatch(updateSearch({ searchIndex: i, updatedResults: data.notes, workspaceId }))
			}
		} catch (error) {
			return rejectWithValue("Updating workspace searches failed")
		}
	}
)

const initialState = {
	searches: [],
	isSearching: false, // is the user ACTIVELY looking at a search tab?
	currentSearch: -1,
	isLoading: false, // are any of the searches actively updating?
	error: null
}

const ensureWorkspace = (state, workspaceId) => ensureWorkspaceTemplate(state, workspaceId, initialState)

export const workspaceSearchSlice = createSlice({
	name: "workspaceSearch",
	initialState: {
		byWorkspace: {},
	},
	reducers: {
		hideSearches: (state, action) => {
			const workspace = ensureWorkspace(state, action.payload)
			workspace.isSearching = false
		},
		showSearches: (state, action) => {
			const workspace = ensureWorkspace(state, action.payload)
			workspace.isSearching = true
		},
		openExistingSearch: (state, action) => {
			const {index: newIndex, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.isSearching = true
			workspace.currentSearch = newIndex
		},
		resetWorkspaceSearches: (state, action) => {
			const workspace = ensureWorkspace(state, action.payload)
			workspace.searches = []
			workspace.isSearching = false
			workspace.currentSearch = -1
		},
		removeWorkspaceSearch: (state, action) => {
			const {index: newIndex, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			if (workspace.searches.length === 1) {
				workspaceSearchSlice.caseReducers.resetWorkspaceSearches(state, {payload: workspaceId})
			} else {
				const deletingIndex = newIndex

				if (
					workspace.currentSearch >= deletingIndex &&
					workspace.currentSearch > 0
				) {
					workspace.currentSearch = workspace.currentSearch - 1
				}
				workspace.searches.splice(deletingIndex, 1)
			}
		},
		updateSearch: (state, action) => {
			const { searchIndex, updatedResults, workspaceId } = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			workspace.searches[searchIndex].results = updatedResults
			workspace.isLoading = false
		}
	},
	extraReducers: (builder) => {
		builder
			// newSearch thunk
			.addCase( newSearch.pending, (state, action) => {
				const { workspaceId } = action.meta.arg
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.isLoading = true
			})
			.addCase( newSearch.fulfilled, (state, action) => {
				const { workspaceId } = action.payload
				const workspace = ensureWorkspace(state, workspaceId)
				const newIndex = (workspace.searches.push(action.payload) - 1)
				workspace.currentSearch = newIndex//push returns length not new index
				workspace.isSearching = true
				workspace.isLoading = false
			})
			.addCase( newSearch.rejected, (state, action) => {
				const { workspaceId } = action.meta.arg
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.error = action.payload || "Failed to create new Workspace Search"
				workspace.isLoading = false
			})
			// updateSearches thunk
			.addCase( updateSearches.pending, (state, action) => {
				const workspace = ensureWorkspace(state, action.meta.arg)
				workspace.isLoading = true
			})
			.addCase( updateSearches.fulfilled, (state, action) => {
				const workspace = ensureWorkspace(state, action.meta.arg)
				workspace.isLoading = false
			})
			.addCase(updateSearches.rejected, (state, action) => {
				const workspace = ensureWorkspace(state, action.meta.arg)
				workspace.error = action.payload
				workspace.isLoading = false
			})
			// workspaceNoteSlice.pasteCopy thunk
			.addCase(pasteCopy.pending, (state, action) => {
				const { workspaceId } = action.meta.arg.targetNote
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.isLoading = true
			})
			.addCase(pasteCopy.fulfilled, (state, action) => {
				const { workspaceId } = action.meta.arg.targetNote
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.isLoading = false
			})
			.addCase(pasteCopy.rejected, (state, action) => {
				const { workspaceId } = action.meta.arg.targetNote
				const workspace = ensureWorkspace(state, workspaceId)
				workspace.isLoading = false
			})
	}
})

export const { hideSearches, updateSearch, showSearches, openExistingSearch, resetWorkspaceSearches, removeWorkspaceSearch } = workspaceSearchSlice.actions

export const selectSearches = (state, workspaceId) => state.workspaceSearch.byWorkspace[workspaceId]?.searches ?? initialState.searches
export const selectIsSearching = (state, workspaceId) => state.workspaceSearch.byWorkspace[workspaceId]?.isSearching ?? initialState.isSearching
export const selectCurrentWorkspaceSearchIndex = (state, workspaceId) => state.workspaceSearch.byWorkspace[workspaceId]?.currentSearch ?? initialState.currentSearch
export const selectIsWorkspaceSearchLoading = (state, workspaceId) => state.workspaceSearch.byWorkspace[workspaceId]?.isLoading ?? initialState.isLoading

export const selectCurrentWorkspaceSearchResults = (workspaceId) => createSelector(
	[
		(state) => selectCurrentWorkspaceSearchIndex(state, workspaceId),
		(state) => selectSearches(state, workspaceId)
	],
	(currentSearchIndex, searches) => {
		if (currentSearchIndex === -1) {
			return []
		}
		return searches[currentSearchIndex]?.results || []
	}
)

export const selectCurrentWorkspaceSearch = (workspaceId) => createSelector(
	[
		(state) => selectCurrentWorkspaceSearchIndex(state, workspaceId),
		(state) => selectSearches(state, workspaceId)
	], // Input selectors
	(currentSearchIndex, searches) => {
		if (currentSearchIndex === -1) {
			return []
		}
		return searches[currentSearchIndex] || []
	}
)


export default workspaceSearchSlice.reducer